import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { Check, ChevronDown, ChevronRight, Download, Eye, Printer, Save, Search, X } from 'lucide-react';
import useFocusTrap from '../../../../hooks/useFocusTrap';
import ActionButtons from '../../../../components/Common/ActionButtons/ActionButtons';
import ConfirmDeleteModal from '../../../../components/Common/ConfirmDeleteModal/ConfirmDeleteModal';
import ccmService from '../../../../services/ccmService';
import { getDisease, listDiseases } from './diseaseRegistry';
import { findArrhythmiaCondition } from './arrhythmiaDisease';
import { findGeneralCondition } from './generalChronicDisease';
import { formatIcdOptionLabel } from '../../../../utils/patientMonitoringIcdUtils';
import { personPickerLabel } from '../shared/PatientCaregiverPickers';
import {
  applyEnrollmentCareTeam,
  applyPatientInsurance,
  describePlanChanges,
  getPath,
  hydrateCareTeamSelection,
  mirrorSignoffStaff,
  mergePlan,
  normalizeLoadedPlan,
  sectionHasPlanChange,
  sectionRemovedLabels,
} from './planState';
import { collectMissingRequired } from './sectionBuilders';
import DiseasePlanForm, { sectionList } from './DiseasePlanForm';
import { buildDiseaseCarePlanPrintHtml, printDiseaseCarePlan, downloadDiseaseCarePlanPdf } from './diseaseCarePlanPdfUtils';
import './DiseaseCarePlan.css';

const DOC_KIND = 'disease-care-plan';

function cleanPlanText(value) {
  return String(value || '').replace(/\s*\(inactive\)\s*$/i, '').trim();
}

function planCardDetails(plan) {
  return {
    diagnosis: cleanPlanText(plan?.diagnosis?.primary),
    startDate: cleanPlanText(plan?.patient?.startDate),
    nextReview: cleanPlanText(plan?.signoff?.nextReview),
    reviewInterval: cleanPlanText(plan?.signoff?.reviewInterval),
    provider: cleanPlanText(plan?.signoff?.providerName || plan?.team?.pcp),
    caregivers: cleanPlanText(plan?.signoff?.managerName || plan?.team?.careManager),
  };
}

const STEP_LABELS = {
  patient: 'Patient',
  diagnosis: 'Diagnosis',
  baseline: 'Baseline',
  risk: 'Risk',
  device: 'Device',
  goals: 'Goals',
  meds: 'Meds',
  monitoring: 'Monitor',
  emergency: 'Emergency',
  education: 'Education',
  signoff: 'Sign-off',
};

function mergePeople(primary, extra) {
  const map = new Map();
  [...(Array.isArray(primary) ? primary : []), ...(Array.isArray(extra) ? extra : [])].forEach((row) => {
    if (!row || row.id == null || String(row.id).trim() === '') return;
    const key = String(row.id);
    if (!map.has(key)) map.set(key, row);
  });
  return Array.from(map.values());
}

function parseContent(raw) {
  if (!raw) return null;
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (value?.kind !== DOC_KIND || !Array.isArray(value.diseases)) return null;
    return value;
  } catch {
    return null;
  }
}

const DiseaseCarePlanWorkspace = forwardRef(function DiseaseCarePlanWorkspace({
  program = 'CCM',
  enrollmentId,
  patient,
  readOnly = false,
  onPlansChange,
  onBillingRefresh,
  practiceCaregivers = [],
  practiceProviders = [],
  canReview = false,
}, ref) {
  const maxDiseases = 6;
  const [loading, setLoading] = useState(program !== 'PCM');
  const [doc, setDoc] = useState({ diseases: [] });
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSection, setActiveSection] = useState('patient');
  const [saved, setSaved] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('edit');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('arrhythmia');
  const [templateSearch, setTemplateSearch] = useState('');
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [planLibrary, setPlanLibrary] = useState([]);
  const creatingDraftRef = useRef(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [planRecord, setPlanRecord] = useState(null);
  const [priorPlans, setPriorPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sectionMenuOpen, setSectionMenuOpen] = useState(false);
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const modalRef = useRef(null);
  const previewRef = useRef(null);
  const scrollStepToTopRef = useRef(false);
  const sectionMenuRef = useRef(null);
  const docBeforeCreateRef = useRef(null);
  const patientRef = useRef(patient);
  patientRef.current = patient;
  const staffRef = useRef({ caregivers: [], providers: [] });
  staffRef.current = {
    caregivers: mergePeople(practiceCaregivers, patient?.caregivers),
    providers: Array.isArray(practiceProviders) ? practiceProviders : [],
  };
  const stampPlan = (plan, currentPatient, diseaseId) => {
    let next = hydrateCareTeamSelection(
      applyPatientInsurance(applyEnrollmentCareTeam(plan, currentPatient, program), currentPatient),
      staffRef.current
    );
    if (next?.diagnosis) {
      const condition = diseaseId === 'arrhythmia'
        ? (findArrhythmiaCondition(currentPatient) || findGeneralCondition(currentPatient))
        : findGeneralCondition(currentPatient);
      if (condition) {
        const primary = formatIcdOptionLabel({
          code: condition.icd_code,
          name: condition.condition_name,
        }) || condition.condition_name || condition.icd_code || '';
        const onset = condition.onset_date ? String(condition.onset_date).slice(0, 10) : '';
        next = {
          ...next,
          diagnosis: {
            ...next.diagnosis,
            primary: primary || next.diagnosis.primary || '',
            date: onset || next.diagnosis.date || '',
          },
        };
      }
    }
    return program === 'PCM' ? mirrorSignoffStaff(next) : next;
  };

  const load = useCallback(async () => {
    if (!enrollmentId) {
      setLoading(false);
      return;
    }
    const currentPatient = patientRef.current;
    try {
      setLoading(true);
      const res = await ccmService.getCarePlans(enrollmentId);
      const rows = Array.isArray(res?.data) ? res.data : [];
      const statusRank = (status) => {
        if (status === 'pending') return 0;
        if (status === 'active') return 1;
        if (status === 'draft') return 2;
        return 3;
      };
      const parsedRows = rows
        .map((row) => ({ row, content: parseContent(row.plan_content) }))
        .filter((item) => item.content && (!item.content.program || item.content.program === program));
      const matches = parsedRows
        .filter((item) => item.row.status !== 'archived')
        .sort((a, b) => {
          if (program === 'PCM') return Number(b.row.id) - Number(a.row.id);
          const rankDiff = statusRank(a.row.status) - statusRank(b.row.status);
          if (rankDiff) return rankDiff;
          return Number(b.row.id) - Number(a.row.id);
        });
      const toDiseases = (content) => (content.diseases || []).map((entry) => {
        const disease = getDisease(entry.diseaseId);
        if (!disease) return null;
        return {
          instanceId: entry.instanceId || `${entry.diseaseId}-${Date.now()}`,
          diseaseId: entry.diseaseId,
          plan: stampPlan(
            normalizeLoadedPlan(mergePlan(disease.createEmpty(currentPatient), entry.plan)),
            currentPatient,
            entry.diseaseId
          ),
        };
      }).filter(Boolean);
      const seenPrior = new Set();
      const priors = [];
      parsedRows
        .filter((item) => item.row.status === 'archived')
        .sort((a, b) => Number(b.row.id) - Number(a.row.id))
        .forEach((match) => {
          const diseases = toDiseases(match.content);
          const diseaseId = diseases[0]?.diseaseId;
          if (!diseaseId || seenPrior.has(diseaseId)) return;
          seenPrior.add(diseaseId);
          priors.push({ diseaseId, plan: diseases[0].plan });
        });
      setPriorPlans(priors);
      if (program === 'PCM') {
        const library = matches
          .map((match) => ({
            id: match.row.id,
            status: match.row.status || 'pending',
            diseases: toDiseases(match.content),
          }))
          .filter((item) => item.diseases.length);
        setPlanLibrary(library);
        if (!creatingDraftRef.current) {
          const first = library[0];
          setDoc({ diseases: first?.diseases || [] });
          setPlanRecord(first ? { id: first.id, status: first.status } : null);
          setActiveIndex(0);
          const firstDisease = first?.diseases?.[0] ? getDisease(first.diseases[0].diseaseId) : null;
          setActiveSection(firstDisease ? sectionList(firstDisease, program)[0].id : 'patient');
        }
      } else if (matches[0]) {
        const diseases = toDiseases(matches[0].content);
        setDoc({ diseases });
        setPlanRecord({ id: matches[0].row.id, status: matches[0].row.status || 'pending' });
        setActiveIndex(0);
        const first = diseases[0] ? getDisease(diseases[0].diseaseId) : null;
        setActiveSection(first ? sectionList(first, program)[0].id : 'patient');
      } else {
        setDoc({ diseases: [] });
        setPlanRecord(null);
      }
      setSaved(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load the care plan');
    } finally {
      setLoading(false);
    }
  }, [enrollmentId, program]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const staff = staffRef.current;
    if (!staff.caregivers.length && !staff.providers.length) return undefined;
    const stamp = (plan) => {
      const next = hydrateCareTeamSelection(plan, staff);
      return program === 'PCM' ? mirrorSignoffStaff(next) : next;
    };
    setDoc((prev) => {
      if (!prev.diseases?.length) return prev;
      return {
        ...prev,
        diseases: prev.diseases.map((item) => ({ ...item, plan: stamp(item.plan) })),
      };
    });
    setPlanLibrary((prev) => prev.map((record) => ({
      ...record,
      diseases: (record.diseases || []).map((item) => ({ ...item, plan: stamp(item.plan) })),
    })));
    return undefined;
  }, [practiceCaregivers, practiceProviders]);

  useEffect(() => {
    if (!editorOpen && !pickerOpen && !previewOpen) return undefined;
    const previousBody = document.body.style.overflow;
    const previousHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousHtml;
    };
  }, [editorOpen, pickerOpen, previewOpen]);

  useEffect(() => {
    if (!scrollStepToTopRef.current || !editorOpen) return;
    scrollStepToTopRef.current = false;
    const body = modalRef.current?.querySelector('.dcp-modal-body');
    if (body) body.scrollTop = 0;
  }, [activeSection, editorOpen]);

  const closeEditor = () => {
    if (sectionMenuOpen) {
      setSectionMenuOpen(false);
      return;
    }
    if (creatingDraft) {
      creatingDraftRef.current = false;
      setCreatingDraft(false);
      setSaved(true);
      if (program === 'PCM') {
        const first = planLibrary[0];
        setDoc({ diseases: first?.diseases || [] });
        setPlanRecord(first ? { id: first.id, status: first.status } : null);
        setActiveIndex(0);
        const restoredDisease = first?.diseases?.[0] ? getDisease(first.diseases[0].diseaseId) : null;
        setActiveSection(restoredDisease ? sectionList(restoredDisease, program)[0].id : 'patient');
      } else {
        const restored = docBeforeCreateRef.current || { diseases: [] };
        setDoc(restored);
        setActiveIndex(0);
        const restoredDisease = restored.diseases?.[0] ? getDisease(restored.diseases[0].diseaseId) : null;
        setActiveSection(restoredDisease ? sectionList(restoredDisease, program)[0].id : 'patient');
      }
    }
    setSectionMenuOpen(false);
    setPreviewOpen(false);
    setEditorOpen(false);
  };

  useFocusTrap(modalRef, editorOpen && !previewOpen, closeEditor);
  useFocusTrap(previewRef, previewOpen, () => setPreviewOpen(false));

  useEffect(() => {
    if (!sectionMenuOpen) return undefined;
    const onPointer = (event) => {
      if (!sectionMenuRef.current?.contains(event.target)) setSectionMenuOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setSectionMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [sectionMenuOpen]);

  const entry = doc.diseases[activeIndex] || null;
  const disease = entry ? getDisease(entry.diseaseId) : null;
  const sections = useMemo(
    () => (disease ? sectionList(disease, program) : []),
    [disease, program]
  );
  const ctx = disease && entry ? disease.derive(entry.plan) : null;
  const staffOptions = useMemo(() => {
    const caregiverRecords = mergePeople(practiceCaregivers, patient?.caregivers);
    const providerRecords = Array.isArray(practiceProviders) ? practiceProviders : [];
    return {
      caregivers: caregiverRecords.map((row) => personPickerLabel(row)).filter(Boolean),
      caregiverRecords,
      providers: providerRecords.map((row) => personPickerLabel(row)).filter(Boolean),
      providerRecords,
    };
  }, [practiceCaregivers, practiceProviders, patient]);
  const sectionIndex = Math.max(0, sections.findIndex((item) => item.id === activeSection));
  const reviewChanges = useMemo(() => {
    if (creatingDraft || !entry || planRecord?.status !== 'pending') return null;
    const diseaseId = entry.diseaseId;
    const active = planLibrary.find((record) => (
      record.status === 'active'
      && record.id !== planRecord?.id
      && record.diseases?.[0]?.diseaseId === diseaseId
    ));
    const baseline = active?.diseases?.[0]?.plan
      || priorPlans.find((item) => item.diseaseId === diseaseId)?.plan
      || null;
    if (!baseline) return null;
    const changes = describePlanChanges(entry.plan, baseline);
    return changes.paths.size ? changes : null;
  }, [creatingDraft, entry, planRecord, planLibrary, priorPlans]);
  const activeSectionHasChanges = Boolean(
    reviewChanges
    && sections[sectionIndex]
    && sectionHasPlanChange(sections[sectionIndex], reviewChanges)
  );
  const activeSectionRemoved = activeSectionHasChanges
    ? sectionRemovedLabels(sections[sectionIndex], reviewChanges)
    : [];

  const updatePlan = (nextPlan) => {
    setSaved(false);
    setDoc((prev) => ({
      ...prev,
      diseases: prev.diseases.map((item, index) => (
        index === activeIndex ? { ...item, plan: nextPlan } : item
      )),
    }));
  };

  const addDisease = (diseaseId) => {
    const definition = getDisease(diseaseId);
    if (!definition) return;
    if (program !== 'PCM' && doc.diseases.some((item) => item.diseaseId === diseaseId)) {
      toast.error('That disease plan is already on this care plan.');
      return;
    }
    const pcmCount = planLibrary.length + (creatingDraft ? 1 : 0);
    if ((program === 'PCM' ? pcmCount : doc.diseases.length) >= maxDiseases) {
      toast.error(program === 'PCM'
        ? 'This patient already has the maximum number of care plans.'
        : 'This care plan already has the maximum number of disease plans.');
      return;
    }
    const next = {
      instanceId: `${diseaseId}-${Date.now()}`,
      diseaseId,
      plan: stampPlan(definition.createEmpty(patient), patient, diseaseId),
    };
    docBeforeCreateRef.current = doc;
    setCreatingDraft(true);
    if (program === 'PCM') {
      creatingDraftRef.current = true;
      setPlanRecord(null);
      setDoc({ diseases: [next] });
      setActiveIndex(0);
    } else {
      setDoc((prev) => ({ ...prev, diseases: [...prev.diseases, next] }));
      setActiveIndex(doc.diseases.length);
    }
    setActiveSection(sectionList(definition, program)[0].id);
    setEditorMode('edit');
    setSaved(false);
    setEditorOpen(true);
  };

  const openListedPlan = (index = 0, mode = 'edit') => {
    if (program !== 'PCM') {
      openEditor(index, mode);
      return;
    }
    const isDraft = creatingDraft && index === planLibrary.length;
    if (isDraft) {
      setActiveIndex(0);
      setEditorMode(readOnly ? 'view' : mode);
      setEditorOpen(true);
      return;
    }
    const record = planLibrary[index];
    if (!record) return;
    setDoc({ diseases: record.diseases });
    setPlanRecord({ id: record.id, status: record.status });
    creatingDraftRef.current = false;
    setCreatingDraft(false);
    setActiveIndex(0);
    const nextDisease = getDisease(record.diseases[0]?.diseaseId);
    setActiveSection(nextDisease ? sectionList(nextDisease, program)[0].id : 'patient');
    setEditorMode(readOnly ? 'view' : mode);
    setSaved(true);
    setEditorOpen(true);
  };

  const removeDisease = async (index) => {
    if (program === 'PCM') {
      const isDraft = creatingDraft && index === planLibrary.length;
      if (!isDraft) {
        const record = planLibrary[index];
        if (!record?.id) return;
        try {
          await ccmService.deleteDiseaseCarePlan(record.id);
        } catch (err) {
          toast.error(err?.response?.data?.message || 'Unable to remove the care plan');
          return;
        }
        const rest = planLibrary.filter((item) => item.id !== record.id);
        setPlanLibrary(rest);
        if (!creatingDraft || planRecord?.id === record.id) {
          const first = rest[0];
          setDoc({ diseases: first?.diseases || [] });
          setPlanRecord(first ? { id: first.id, status: first.status } : null);
        }
      } else {
        creatingDraftRef.current = false;
        setCreatingDraft(false);
        const first = planLibrary[0];
        setDoc({ diseases: first?.diseases || [] });
        setPlanRecord(first ? { id: first.id, status: first.status } : null);
      }
      setEditorOpen(false);
      setSaved(true);
      setDeleteTarget(null);
      toast.success('Care plan removed');
      if (typeof onBillingRefresh === 'function') onBillingRefresh();
      return;
    }
    setDoc((prev) => ({
      ...prev,
      diseases: prev.diseases.filter((_, itemIndex) => itemIndex !== index),
    }));
    setCreatingDraft(false);
    setEditorOpen(false);
    setSaved(true);
    setDeleteTarget(null);
    toast.success('Care plan removed');
  };

  const requestDelete = (index) => {
    if (program === 'PCM') {
      const isDraft = creatingDraft && index === planLibrary.length;
      const record = isDraft ? doc.diseases[0] : planLibrary[index]?.diseases?.[0];
      if (!record) return;
      const label = getDisease(record.diseaseId)?.label || 'this care plan';
      setDeleteTarget({ index, label });
      return;
    }
    const item = doc.diseases[index];
    if (!item) return;
    const label = getDisease(item.diseaseId)?.label || 'this care plan';
    setDeleteTarget({ index, label });
  };

  const planIdForAction = (index) => (
    program === 'PCM' && index != null ? planLibrary[index]?.id : planRecord?.id
  );

  const approvePlan = async (index) => {
    const targetId = planIdForAction(index);
    if (!targetId || saving) return;
    try {
      setSaving(true);
      await ccmService.approveCarePlan(targetId);
      toast.success('Care plan approved and active');
      if (Number(planRecord?.id) === Number(targetId)) {
        setSectionMenuOpen(false);
        setEditorOpen(false);
      }
      await load();
      if (typeof onBillingRefresh === 'function') onBillingRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to approve the care plan');
    } finally {
      setSaving(false);
    }
  };

  const rejectPlan = (index) => {
    const targetId = planIdForAction(index);
    if (!targetId || saving) return;
    setRejectTargetId(targetId);
  };

  const confirmRejectPlan = async () => {
    const targetId = rejectTargetId;
    if (!targetId || saving) return;
    try {
      setSaving(true);
      await ccmService.rejectCarePlan(targetId);
      toast.success('Care plan changes rejected. The active care plan is unchanged.');
      setRejectTargetId(null);
      if (Number(planRecord?.id) === Number(targetId)) {
        setSectionMenuOpen(false);
        setEditorOpen(false);
      }
      await load();
      if (typeof onBillingRefresh === 'function') onBillingRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to reject the care plan');
    } finally {
      setSaving(false);
    }
  };

  const getPlanToExport = (index = 0) => {
    let targetDisease = null;
    let targetStatus = 'pending';
    if (program === 'PCM') {
      const isDraft = creatingDraft && index === planLibrary.length;
      if (isDraft) {
        targetDisease = doc.diseases[0];
        targetStatus = 'draft';
      } else {
        const record = planLibrary[index] || (doc.diseases[0] ? { diseases: doc.diseases, status: planRecord?.status || 'pending' } : null);
        targetDisease = record?.diseases?.[0] || doc.diseases[0];
        targetStatus = record?.status || planRecord?.status || 'pending';
      }
    } else {
      targetDisease = doc.diseases[index] || doc.diseases[0];
      targetStatus = planRecord?.status || 'active';
    }
    if (!targetDisease) return null;
    const diseaseMeta = getDisease(targetDisease.diseaseId);
    return {
      plan: targetDisease.plan,
      diseaseMeta,
      status: targetStatus,
    };
  };

  const openPlanPreview = () => {
    const plan = entry?.plan;
    if (!plan) {
      toast.warning('No care plan data available to preview.');
      return;
    }
    const status = program === 'PCM'
      ? (creatingDraft ? 'draft' : (planRecord?.status || 'pending'))
      : (planRecord?.status || 'active');
    const html = buildDiseaseCarePlanPrintHtml(plan, patientRef.current || patient || {}, {
      program,
      disease,
      diseaseLabel: disease?.label || 'Care plan',
      status,
    });
    setPreviewHtml(html);
    setPreviewOpen(true);
  };

  const handlePrintPlan = (index = 0) => {
    const data = getPlanToExport(index);
    if (!data || !data.plan) {
      toast.warning('No care plan data available to print.');
      return;
    }
    const currentPatient = patientRef.current || patient || {};
    const ok = printDiseaseCarePlan(data.plan, currentPatient, {
      program,
      disease: data.diseaseMeta,
      diseaseLabel: data.diseaseMeta?.label || 'Care plan',
      status: data.status,
    });
    if (!ok) {
      toast.info('Please allow pop-ups to print the care plan.');
    }
  };

  const handleDownloadPlan = async (index = 0) => {
    const data = getPlanToExport(index);
    if (!data || !data.plan) {
      toast.warning('No care plan data available to download.');
      return;
    }
    const currentPatient = patientRef.current || patient || {};
    try {
      toast.info('Generating PDF download…');
      await downloadDiseaseCarePlanPdf(data.plan, currentPatient, {
        program,
        disease: data.diseaseMeta,
        diseaseLabel: data.diseaseMeta?.label || 'Care plan',
        status: data.status,
      });
      toast.success('Care plan downloaded successfully.');
    } catch (err) {
      console.error('Error downloading care plan PDF:', err);
      toast.error('Unable to generate PDF. Please try using the Print option to save as PDF.');
    }
  };

  useImperativeHandle(ref, () => ({
    openCreate,
    viewPlan: (index = 0) => openListedPlan(index, 'view'),
    editPlan: (index = 0) => openListedPlan(index, 'edit'),
    deletePlan: (index = 0) => requestDelete(index),
    printPlan: (index = 0) => handlePrintPlan(index),
    downloadPlan: (index = 0) => handleDownloadPlan(index),
    approvePlan,
    rejectPlan,
    reload: load,
  }));

  useEffect(() => {
    if (typeof onPlansChange !== 'function') return;
    if (program === 'PCM') {
      const saved = planLibrary.map((record) => {
        const item = record.diseases[0] || {};
        return {
          instanceId: item.instanceId || `plan-${record.id}`,
          diseaseId: item.diseaseId,
          label: getDisease(item.diseaseId)?.label || 'Care plan',
          planId: record.id,
          status: record.status || 'pending',
          ...planCardDetails(item.plan),
        };
      });
      if (creatingDraft && doc.diseases[0]) {
        const item = doc.diseases[0];
        saved.push({
          instanceId: item.instanceId,
          diseaseId: item.diseaseId,
          label: getDisease(item.diseaseId)?.label || 'Care plan',
          planId: null,
          status: 'unsaved',
          ...planCardDetails(item.plan),
        });
      }
      onPlansChange(saved);
      return;
    }
    onPlansChange(doc.diseases.map((item) => ({
      instanceId: item.instanceId,
      diseaseId: item.diseaseId,
      label: getDisease(item.diseaseId)?.label || item.diseaseId,
      planId: planRecord?.id || null,
      status: creatingDraft || !planRecord?.id ? 'unsaved' : (planRecord.status || 'pending'),
    })));
  }, [doc.diseases, onPlansChange, planRecord, creatingDraft, program, planLibrary]);

  const save = async () => {
    if (readOnly || saving) return;
    if (!doc.diseases.length) {
      toast.error('Choose a disease plan first.');
      return;
    }
    const activeEntry = doc.diseases[activeIndex];
    const activeDisease = activeEntry ? getDisease(activeEntry.diseaseId) : null;
    if (activeDisease && activeEntry) {
      const steps = sectionList(activeDisease, program);
      const derived = activeDisease.derive(activeEntry.plan);
      for (let index = 0; index < steps.length; index += 1) {
        const missing = collectMissingRequired(steps[index].cards, activeEntry.plan, derived);
        if (missing.length) {
          setShowRequiredErrors(true);
          setActiveSection(steps[index].id);
          setEditorOpen(true);
          const names = missing.map((item) => item.label).join(', ');
          toast.error(missing.length > 3
            ? 'Fill the required fields on this step before saving.'
            : `Fill the required fields before saving: ${names}.`);
          return;
        }
      }
    }
    if (program !== 'PCM') {
      setSaved(true);
      setCreatingDraft(false);
      setEditorOpen(false);
      toast.success('Care plan saved');
      return;
    }
    const missingProvider = doc.diseases.some((item) => !String(getPath(item.plan, 'team.pcp') || '').trim());
    if (missingProvider) {
      toast.error('Select a provider on this step before saving.');
      setActiveSection('patient');
      return;
    }
    if (!enrollmentId) {
      toast.error('This patient is not enrolled in PCM yet.');
      return;
    }
    const planDisease = getDisease(doc.diseases[0]?.diseaseId);
    try {
      setSaving(true);
      const response = await ccmService.saveDiseaseCarePlan(enrollmentId, {
        program: 'PCM',
        plan_name: planDisease?.label ? `${planDisease.label} PCM care plan` : 'PCM care plan',
        plan_id: creatingDraft || planRecord?.status !== 'pending' ? null : planRecord.id,
        plan_content: {
          kind: DOC_KIND,
          version: 1,
          program: 'PCM',
          diseases: doc.diseases.map((item) => ({
            instanceId: item.instanceId,
            diseaseId: item.diseaseId,
            plan: item.plan,
          })),
        },
      });
      setSaved(true);
      creatingDraftRef.current = false;
      setCreatingDraft(false);
      setEditorOpen(false);
      toast.success(response?.message || 'Care plan saved as pending');
      await load();
      if (typeof onBillingRefresh === 'function') onBillingRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to save the care plan');
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (index = 0, mode = 'edit') => {
    if (doc.diseases[index]) {
      setActiveIndex(index);
      const nextDisease = getDisease(doc.diseases[index].diseaseId);
      setActiveSection(nextDisease ? sectionList(nextDisease, program)[0].id : 'patient');
    }
    setEditorMode(readOnly ? 'view' : mode);
    setEditorOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setTemplateSearch('');
  };

  const openCreate = () => {
    const defaultId = program === 'PCM' ? 'arrhythmia' : (available[0]?.id || 'arrhythmia');
    setSelectedTemplateId(defaultId);
    setTemplateSearch('');
    setPickerOpen(true);
  };

  const chooseCarePlan = (diseaseId) => {
    if (!diseaseId) return;
    closePicker();
    addDisease(diseaseId);
  };

  const formReadOnly = readOnly || editorMode === 'view';
  const missingRequired = showRequiredErrors && entry && !formReadOnly
    ? collectMissingRequired(sections[sectionIndex]?.cards, entry.plan, ctx)
    : [];
  const invalidPaths = missingRequired.map((item) => item.path);

  const leaveSection = (targetId, targetIndex) => {
    if (!formReadOnly && targetIndex > sectionIndex) {
      for (let index = sectionIndex; index < targetIndex; index += 1) {
        const missing = collectMissingRequired(sections[index]?.cards, entry?.plan, ctx);
        if (missing.length) {
          setShowRequiredErrors(true);
          setSectionMenuOpen(false);
          if (index !== sectionIndex) setActiveSection(sections[index].id);
          const names = missing.map((item) => item.label).join(', ');
          toast.error(missing.length > 3
            ? 'Fill the required fields on this step before continuing.'
            : `Fill the required fields before continuing: ${names}.`);
          return;
        }
      }
    }
    setShowRequiredErrors(false);
    scrollStepToTopRef.current = true;
    setActiveSection(targetId);
    setSectionMenuOpen(false);
  };

  if (loading) {
    return <div className="dcp-page-loading">Loading care plan…</div>;
  }

  const available = program === 'PCM'
    ? listDiseases().filter((item) => item.id === 'arrhythmia')
    : listDiseases().filter((item) => !doc.diseases.some((entryItem) => entryItem.diseaseId === item.id));
  const planChoiceLabel = (item) => {
    if (item.id === 'arrhythmia') return 'Arrhythmia Template';
    if (item.id === 'general') return 'Custom Template';
    return `${item.label} Template`;
  };
  const planChoiceSummary = (item) => {
    if (item.id === 'arrhythmia') {
      return 'Atrial fibrillation and other cardiac arrhythmias with device and medication tracking.';
    }
    if (item.id === 'general') {
      return 'Standard care plan framework customizable for any chronic condition.';
    }
    return item.summary;
  };
  const filteredTemplates = available.filter((item) => {
    if (!templateSearch.trim()) return true;
    const query = templateSearch.toLowerCase().trim();
    const label = planChoiceLabel(item).toLowerCase();
    const summary = (planChoiceSummary(item) || '').toLowerCase();
    return label.includes(query) || summary.includes(query);
  });
  const pickerModal = pickerOpen && typeof document !== 'undefined' ? createPortal(
    <div className="dcp-overlay" role="presentation" onClick={closePicker}>
      <div
        className="dcp-select-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dcp-select-title"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') closePicker();
        }}
      >
        <div className="dcp-select-head">
          <div>
            <h2 id="dcp-select-title">Choose Template</h2>
            <p className="dcp-select-copy">
              {program === 'PCM'
                ? 'Select a care plan template for Principal Care Management.'
                : 'Select a template to create this care plan.'}
            </p>
          </div>
          <button type="button" className="dcp-close" onClick={closePicker} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {available.length === 0 ? (
          <p className="dcp-empty">Every available template already has a care plan.</p>
        ) : (
          <>
            <div className="dcp-template-search-wrap">
              <Search size={16} className="dcp-template-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="dcp-template-search-input"
                placeholder="Search templates..."
                value={templateSearch}
                style={{ paddingLeft: '40px', paddingRight: '36px' }}
                onChange={(e) => {
                  const val = e.target.value;
                  setTemplateSearch(val);
                  const q = val.toLowerCase().trim();
                  const matched = available.filter((item) => {
                    if (!q) return true;
                    return planChoiceLabel(item).toLowerCase().includes(q)
                      || (planChoiceSummary(item) || '').toLowerCase().includes(q);
                  });
                  if (matched.length > 0 && !matched.some((item) => item.id === selectedTemplateId)) {
                    setSelectedTemplateId(matched[0].id);
                  }
                }}
              />
              {templateSearch ? (
                <button
                  type="button"
                  className="dcp-template-search-clear"
                  onClick={() => {
                    setTemplateSearch('');
                    if (available.length > 0 && !available.some((item) => item.id === selectedTemplateId)) {
                      setSelectedTemplateId(available[0].id);
                    }
                  }}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              ) : null}
            </div>

            {filteredTemplates.length === 0 ? (
              <p className="dcp-empty" style={{ margin: '24px 0', textAlign: 'center' }}>
                No templates found matching &ldquo;{templateSearch}&rdquo;
              </p>
            ) : (
              <div className="dcp-select-list">
                {filteredTemplates.map((item) => {
                  const isSelected = (selectedTemplateId || filteredTemplates[0]?.id) === item.id;
                  return (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      className={`dcp-select-option${isSelected ? ' is-selected' : ''}`}
                      onClick={() => setSelectedTemplateId(item.id)}
                      onDoubleClick={() => chooseCarePlan(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedTemplateId(item.id);
                        }
                      }}
                    >
                      <div className="dcp-select-option-header">
                        <div className="dcp-select-radio">
                          <div className={`dcp-radio-circle${isSelected ? ' is-checked' : ''}`}>
                            {isSelected ? <div className="dcp-radio-dot" /> : null}
                          </div>
                        </div>
                        <div className="dcp-select-option-title-wrap">
                          <div className="dcp-select-title-row">
                            <strong>{planChoiceLabel(item)}</strong>
                            {program === 'PCM' ? (
                              <span className="dcp-template-tag">PCM</span>
                            ) : null}
                          </div>
                          <span>{planChoiceSummary(item)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div className="dcp-select-footer">
          <button type="button" className="dcp-btn-secondary" onClick={closePicker}>
            Cancel
          </button>
          <button
            type="button"
            className="dcp-btn-primary"
            disabled={filteredTemplates.length === 0}
            onClick={() => {
              const target = filteredTemplates.find((t) => t.id === selectedTemplateId) || filteredTemplates[0];
              if (target) chooseCarePlan(target.id);
            }}
          >
            Use Template
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;
  const modal = editorOpen && typeof document !== 'undefined' ? createPortal(
    <div className="dcp-overlay" role="presentation">
      <div
        className={`dcp-modal${program === 'PCM' ? ' dcp-modal--pcm' : ''}`}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dcp-modal-title"
      >
        <div className="dcp-modal-head">
          <div>
            <h2 id="dcp-modal-title">{editorMode === 'create' ? `New ${program} care plan` : (disease?.title || `${program} care plan`)}</h2>
            {/* <p>
              {editorMode === 'create'
                ? 'Choose a condition to start this care plan.'
                : (entry?.plan?.diagnosis?.primary || 'Patient and care team')}
              {editorMode !== 'create' && !saved && doc.diseases.length ? ' · Unsaved changes' : ''}
            </p> */}
          </div>
          <div className="dcp-modal-head-actions">
            {program !== 'PCM' && editorMode !== 'create' && entry ? (
              <>
                <button
                  type="button"
                  className="dcp-modal-action-btn"
                  onClick={() => handlePrintPlan(activeIndex)}
                  title="Print care plan"
                >
                  <Printer size={15} />
                  <span>Print</span>
                </button>
                <button
                  type="button"
                  className="dcp-modal-action-btn"
                  onClick={() => handleDownloadPlan(activeIndex)}
                  title="Download care plan (PDF)"
                >
                  <Download size={15} />
                  <span>Download</span>
                </button>
              </>
            ) : null}
            <button type="button" className="dcp-close" onClick={closeEditor} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {editorMode !== 'create' && entry && sections.length > 0 ? (
          <div className="dcp-nav">
            <div className="dcp-nav-titlewrap" ref={sectionMenuRef}>
              <span className="dcp-nav-step">Step {String(sectionIndex + 1).padStart(2, '0')} of {String(sections.length).padStart(2, '0')}</span>
              <button
                type="button"
                className="dcp-nav-current"
                aria-expanded={sectionMenuOpen}
                aria-haspopup="menu"
                onClick={() => setSectionMenuOpen((open) => !open)}
              >
                <span>{sections[sectionIndex]?.label || 'Section'}</span>
                <ChevronDown size={18} />
              </button>
              {sectionMenuOpen ? (
                <div className="dcp-section-menu" role="menu">
                  {sections.map((section, index) => {
                    const done = index < sectionIndex;
                    const active = activeSection === section.id;
                    const changed = reviewChanges ? sectionHasPlanChange(section, reviewChanges) : false;
                    return (
                      <button
                        key={section.id}
                        type="button"
                        role="menuitem"
                        className={`dcp-section-menu-item${active ? ' is-active' : ''}${done && !active ? ' is-done' : ''}${changed ? ' has-change' : ''}`}
                        onClick={() => leaveSection(section.id, index)}
                      >
                        <span className="dcp-section-menu-n">{String(index + 1).padStart(2, '0')}</span>
                        <span>{section.label}{changed ? ' · changed' : ''}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {editorMode !== 'create' && entry && sections.length > 0 ? (
          <div className="dcp-stepper" role="tablist" aria-label="Care plan steps">
            {sections.map((section, index) => {
              const done = index < sectionIndex;
              const active = activeSection === section.id;
              const stepName = STEP_LABELS[section.id] || section.label;
              const numbered = program === 'PCM';
              const changed = reviewChanges ? sectionHasPlanChange(section, reviewChanges) : false;
              return (
                <React.Fragment key={section.id}>
                  {index > 0 ? <div className={`dcp-step-line${index <= sectionIndex ? ' is-on' : ''}`} /> : null}
                  <button
                    type="button"
                    className={`dcp-step${numbered ? ' is-numbered' : ''}${active ? ' is-on' : ''}${done ? ' is-done' : ''}${changed ? ' has-change' : ''}`}
                    onClick={() => leaveSection(section.id, index)}
                    aria-current={active ? 'step' : undefined}
                    aria-label={numbered ? `${index + 1}. ${stepName}` : undefined}
                  >
                    <span className="dcp-step-dot">
                      {numbered
                        ? index + 1
                        : (done && !active ? <Check size={10} strokeWidth={3} /> : active ? <span className="dcp-step-inner" /> : null)}
                    </span>
                    {numbered ? (
                      <span className="dcp-step-tip" role="tooltip">{stepName}</span>
                    ) : (
                      <span className="dcp-step-label">{stepName}</span>
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        ) : null}

        <div className="dcp-modal-body">
          {editorMode === 'create' ? (
            <div className="dcp-picker">
              <p>{program === 'PCM' ? 'Choose the one condition for this PCM plan.' : 'Choose the condition for this care plan.'}</p>
              {available.length === 0 ? (
                <p className="dcp-empty">Every available condition already has a care plan.</p>
              ) : (
                <div className="dcp-picker-grid">
                  {available.map((item) => (
                    <button key={item.id} type="button" className="dcp-picker-card" onClick={() => addDisease(item.id)}>
                      <strong>{item.label}</strong>
                      <span>{item.summary}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : entry ? (
            <>
              {activeSectionHasChanges ? (
                <div className="dcp-change-banner" role="status">
                  <strong>Highlighted fields on this step changed from the active care plan.</strong>
                  {activeSectionRemoved.length ? (
                    <span>
                      {' '}Removed: {activeSectionRemoved.slice(0, 4).join(', ')}
                      {activeSectionRemoved.length > 4 ? '…' : ''}.
                    </span>
                  ) : null}
                </div>
              ) : null}
              <DiseasePlanForm
                disease={disease}
                program={program}
                plan={entry.plan}
                ctx={ctx}
                staffOptions={staffOptions}
                sectionId={sections[sectionIndex]?.id}
                readOnly={formReadOnly}
                invalidPaths={invalidPaths}
                changes={reviewChanges}
                onChange={updatePlan}
              />
            </>
          ) : null}
        </div>

        <div className="dcp-modal-foot">
          {editorMode !== 'create' && entry && sectionIndex > 0 ? (
            <button
              type="button"
              className="dcp-btn-secondary"
              onClick={() => leaveSection(sections[sectionIndex - 1].id, sectionIndex - 1)}
            >
              Previous
            </button>
          ) : (
            <button type="button" className="dcp-btn-secondary" onClick={closeEditor}>
              Cancel
            </button>
          )}
          <div className="dcp-foot-right">
            {canReview && editorMode !== 'create' && planRecord?.status === 'pending' && planRecord?.id ? (
              <>
                <button type="button" className="dcp-btn-reject" onClick={() => rejectPlan()} disabled={saving}>
                  Reject
                </button>
                <button type="button" className="dcp-btn-primary" onClick={() => approvePlan()} disabled={saving}>
                  Approve
                </button>
              </>
            ) : null}
            {editorMode !== 'create' && entry && sectionIndex < sections.length - 1 ? (
              <button
                type="button"
                className="dcp-btn-primary"
                onClick={() => leaveSection(sections[sectionIndex + 1].id, sectionIndex + 1)}
              >
                Next <ChevronRight size={18} />
              </button>
            ) : null}
            {editorMode !== 'create' && entry && sections.length > 0 && sectionIndex === sections.length - 1 ? (
              <button type="button" className="dcp-btn-secondary dcp-btn-preview" onClick={openPlanPreview}>
                <Eye size={18} />
                Preview care plan
              </button>
            ) : null}
            {editorMode !== 'create' && !formReadOnly && sections.length > 0 && sectionIndex === sections.length - 1 ? (
              <button type="button" className="dcp-btn-primary" onClick={save} disabled={doc.diseases.length === 0 || saving}>
                <Save size={18} />
                Save care plan
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  const previewModal = previewOpen && typeof document !== 'undefined' ? createPortal(
    <div className="dcp-overlay dcp-preview-overlay" role="presentation">
      <div
        className="dcp-preview-modal"
        ref={previewRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dcp-preview-title"
      >
        <div className="dcp-modal-head">
          <div>
            <h2 id="dcp-preview-title">Care plan preview</h2>
            <p>Scroll to read the full care plan.</p>
          </div>
          <button type="button" className="dcp-close" onClick={() => setPreviewOpen(false)} aria-label="Close preview">
            <X size={20} />
          </button>
        </div>
        <div className="dcp-preview-body">
          <iframe
            className="dcp-preview-frame"
            title="Care plan preview"
            srcDoc={previewHtml}
          />
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  const hasPlans = doc.diseases.length > 0;
  const pcmWithoutPlan = program === 'PCM' && !hasPlans;

  return (
    <div className="dcp-page">
      <div className="dcp-page-card">
        {pcmWithoutPlan ? (
          !readOnly ? (
            <div className="dcp-empty-center">
              <button type="button" className="dcp-btn-primary" onClick={openCreate}>
                Create care plan
              </button>
            </div>
          ) : (
            <p className="dcp-empty dcp-empty--center">No care plan yet.</p>
          )
        ) : (
        <>
        <div className="dcp-page-head">
          <div>
            <h3>{program} care plan</h3>
            <p>
              {program === 'PCM'
                ? 'Open a plan to fill diagnosis, goals, medicines, and follow-up.'
                : 'One plan per chronic condition. Open the plan to fill each disease.'}
            </p>
          </div>
          {!readOnly && doc.diseases.length < maxDiseases ? (
            <button type="button" className="dcp-btn-primary" onClick={openCreate}>
              Create care plan
            </button>
          ) : null}
        </div>
        {doc.diseases.length === 0 ? (
          <p className="dcp-empty">No care plan yet.</p>
        ) : (
          <ul className="dcp-list">
            {doc.diseases.map((item, index) => {
              const meta = getDisease(item.diseaseId);
              return (
                <li key={item.instanceId}>
                  <div>
                    <strong>{meta?.label || item.diseaseId}</strong>
                    <span>{item.plan?.diagnosis?.primary || 'Condition not named yet'}</span>
                  </div>
                  <ActionButtons
                    onView={() => openEditor(index, 'view')}
                    onEdit={readOnly ? undefined : () => openEditor(index, 'edit')}
                    onDelete={readOnly ? undefined : () => requestDelete(index)}
                    hideEdit={readOnly}
                    hideDelete={readOnly}
                  />
                </li>
              );
            })}
          </ul>
        )}
        </>
        )}
      </div>
      {pickerModal}
      {modal}
      {previewModal}
      {typeof document !== 'undefined' ? createPortal(
        <>
          <ConfirmDeleteModal
            show={deleteTarget != null}
            title="Confirm Delete"
            className="dcp-delete-confirm"
            message={(
              <>
                Are you sure you want to delete the care plan
                {deleteTarget?.label ? <> for <strong>{deleteTarget.label}</strong></> : ''}?
              </>
            )}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={() => {
              if (deleteTarget == null) return;
              removeDisease(deleteTarget.index);
            }}
          />
          <ConfirmDeleteModal
            show={rejectTargetId != null}
            title="Reject care plan changes"
            className="dcp-delete-confirm"
            message="Reject these care plan changes? The active care plan stays as it is."
            warning=""
            confirmLabel="Reject"
            busyLabel="Rejecting..."
            isDeleting={saving}
            onCancel={() => {
              if (!saving) setRejectTargetId(null);
            }}
            onConfirm={confirmRejectPlan}
          />
        </>,
        document.body
      ) : null}
    </div>
  );
});

export default DiseaseCarePlanWorkspace;
